import isNumber from 'lodash/isNumber';

import { getI18nLocale, Locale, translate } from '@front/i18n';

import { Salutation } from '../types/commons';
import { Country } from '../types/country';
import { CURRENCIES_BY_COUNTRY } from '../constants/country';

import { getCurrentCurrency } from './country';
import { formatFloatNumber } from './numbers';
import { removeSpaces } from './strings';
import { getIntlInstance } from './intl';

const t = translate('prices');

export const formatMinutes = (duration: number) => t('durations.minutes', { minutes: duration || 0 });

export const formatDuration = (duration: number = 0) => {
  const hours = Math.floor(duration / 60);
  const minutes = duration % 60;

  return hours > 0
    ? t('durations.hours_minutes', {
        hours,
        minutes: minutes === 0 ? '' : String(minutes).padStart(2, '0'),
      })
    : t('durations.minutes', { minutes });
};

export type NumberFormatOptions = {
  precision?: number;
  locale?: Locale;
  country?: Country;
};

type IntlOptions = Intl.NumberFormatOptions;

const toIntlOptions = (options: NumberFormatOptions): IntlOptions => {
  const intlOptions: IntlOptions = {};
  const { precision } = options;

  if (isNumber(precision)) {
    intlOptions.minimumFractionDigits = precision;
    intlOptions.maximumFractionDigits = precision;
  }

  return intlOptions;
};

const formatCurrency = (amount: number = 0, options: NumberFormatOptions = {}) => {
  let locale = options.locale || getI18nLocale();
  if (locale === 'fr-CH') {
    locale = 'de-CH';
  }

  return getIntlInstance(Intl.NumberFormat, locale, {
    style: 'currency',
    currency: options.country ? CURRENCIES_BY_COUNTRY[options.country] : getCurrentCurrency(),
    ...toIntlOptions(options),
  }).format(amount / 100);
};

export const formatPrice = (price?: number, options?: NumberFormatOptions) =>
  Number.isFinite(price) ? formatCurrency(price, options) : '';

export const formatPriceWithDuration = (price: number, duration?: number | null) =>
  duration
    ? t('.price_duration', {
        price: formatPrice(price),
        duration: formatDuration(duration),
      })
    : formatPrice(price);

export const formatPricePerHour = (price?: number, options?: NumberFormatOptions) =>
  Number.isFinite(price) ? t('.per_hour', { price: formatCurrency(price, options) }) : '';

export const formatPricePerSession = (price?: number) =>
  Number.isFinite(price) ? t('.per_session', { price: formatCurrency(price) }) : '';

export const formatPricePerWeek = (price?: number) =>
  Number.isFinite(price) ? t('.per_week', { price: formatCurrency(price) }) : '';

export const formatPricePerMonth = (price?: number) =>
  Number.isFinite(price) ? t('.per_month', { price: formatCurrency(price) }) : '';

type FormatPriceBeforeAndAfterTaxParams = {
  totalPrice?: number;
  totalPriceAfterTax?: number;
};

export const formatPriceBeforeAndAfterTax = ({
  totalPriceAfterTax,
  totalPrice,
}: FormatPriceBeforeAndAfterTaxParams) => {
  const hasTotalPrice = Number.isFinite(totalPrice);
  const hasTotalPriceAfterTax = Number.isFinite(totalPriceAfterTax);

  if (hasTotalPrice && hasTotalPriceAfterTax) {
    return t('.before_and_after_tax', {
      totalPrice: formatCurrency(totalPrice),
      totalPriceAfterTax: formatCurrency(totalPriceAfterTax),
    });
  }

  if (hasTotalPrice) return formatCurrency(totalPrice);

  return '';
};

type SalutationVariant = 'default' | 'short' | 'thisCustomer' | 'theCustomer';

type FormatSalutationOptions = {
  variant?: SalutationVariant;
};

const SALUTATION_KEYS: Record<Salutation, Record<SalutationVariant, string>> = {
  'M.': {
    default: 'man',
    short: 'man_short',
    theCustomer: 'man_customer',
    thisCustomer: 'man_this_customer',
  },
  Mme: {
    default: 'woman',
    short: 'woman_short',
    theCustomer: 'woman_customer',
    thisCustomer: 'woman_this_customer',
  },
};

export const formatSalutation = (salutation: Salutation, { variant = 'default' }: FormatSalutationOptions = {}) =>
  t(`salutations.${SALUTATION_KEYS[salutation][variant]}`);

export type Separator = 'and' | 'or' | 'plus';

const SEPARATOR_SYMBOLS: Partial<Record<Separator, string>> = {
  plus: '+',
};

export const formatStringListWithSeparator = (
  stringsList?: string[],
  { separator = 'and' }: { separator?: Separator } = {},
) => {
  if (!stringsList || stringsList.length === 0) return '';
  if (stringsList.length === 1) return stringsList[0];

  const firstPartOfString = stringsList.slice(0, -1).join(', ');
  const lastString = stringsList[stringsList.length - 1];

  return `${firstPartOfString} ${SEPARATOR_SYMBOLS[separator] || t(`base.${separator}`)} ${lastString}`;
};

export const formatRating = (rate: number | null, maxRate?: number) => {
  const formattedRate = typeof rate === 'number' ? formatFloatNumber(Math.round(rate * 10) / 10) : '0';

  return maxRate ? `${formattedRate}/${maxRate}` : formattedRate;
};

type FormatOrdinalProps = {
  numberToFormat: number;
  options?: { superscript?: boolean; salutation?: Salutation };
};

export const formatOrdinal = ({ numberToFormat, options = {} }: FormatOrdinalProps): string => {
  const { superscript = false, salutation = 'Mme' } = options;
  const locale = getI18nLocale();
  const pr = getIntlInstance(Intl.PluralRules, locale, { type: 'ordinal' });

  const ordinal = t(`base.ordinal_suffixes.plural_${pr.select(numberToFormat)}`, { salutation });

  return superscript ? `${numberToFormat}^${ordinal}^` : `${numberToFormat}${ordinal}`;
};

export const formatPercentage = (percentage: number, options?: Intl.NumberFormatOptions): string => {
  const result = getIntlInstance(Intl.NumberFormat, getI18nLocale(), {
    style: 'percent',
    ...options,
  }).format(percentage / 100);

  /**
   * Removing spaces from the formatted value is a requirement from the brand team
   * https://wecasa.slack.com/archives/C02PPDJN4FN/p1700237895118459?thread_ts=1700237380.947359&cid=C02PPDJN4FN
   */
  return removeSpaces(result);
};

type FormatHoursPerCalendarUnitParams = {
  value: number;
  calendarUnit: 'day' | 'week' | 'month';
};

export const formatHoursPerCalendarUnit = ({ value, calendarUnit }: FormatHoursPerCalendarUnitParams) => {
  switch (calendarUnit) {
    case 'day':
      return t('calendar_unit.hours_per_day', { value });
    case 'week':
      return t('calendar_unit.hours_per_week', { value });
    case 'month':
      return t('calendar_unit.hours_per_month', { value });
    default:
      return '';
  }
};
